import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  UserMinus, Search, Trash2, Loader2, Shield,
  AlertTriangle, CheckCircle, Users
} from 'lucide-react';
import { API } from '../config/api';

export default function AdminCleanupPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [removing, setRemoving] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (user && user.role !== 'owner' && user.role !== 'admin') {
      navigate('/');
    }
  }, [user]);

  const identifyTestUsers = async () => {
    setLoading(true);
    setResults(null);
    setReport(null);
    try {
      const res = await fetch(`${API}/admin/cleanup/identify-test-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setResults(data);
        // Pre-select all test users (not uncertain ones)
        setSelected(new Set((data.test_users || []).map(u => u.user_id)));
      } else {
        toast.error('Failed to scan users');
      }
    } catch { toast.error('Scan failed'); }
    finally { setLoading(false); }
  };

  const toggleUser = (userId) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const removeSelected = async () => {
    if (selected.size === 0) { toast.error('No users selected'); return; }
    if (!window.confirm(`Are you sure you want to permanently remove ${selected.size} user(s) and ALL their data? This cannot be undone.`)) return;

    setRemoving(true);
    try {
      const res = await fetch(`${API}/admin/cleanup/remove-test-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_ids: Array.from(selected) }),
      });
      if (res.ok) {
        const data = await res.json();
        setReport(data.report);
        setResults(null);
        setSelected(new Set());
        toast.success(`Removed ${data.report?.users_removed || 0} test users`);
      } else {
        toast.error('Cleanup failed');
      }
    } catch { toast.error('Cleanup failed'); }
    finally { setRemoving(false); }
  };

  const UserRow = ({ u, showCheckbox = true, isUncertain = false }) => (
    <div className={`flex items-center gap-3 p-3 rounded-lg ${isUncertain ? 'bg-amber-500/5 border border-amber-500/20' : 'bg-muted/20 border border-border'}`}>
      {showCheckbox && (
        <input
          type="checkbox"
          checked={selected.has(u.user_id)}
          onChange={() => toggleUser(u.user_id)}
          className="rounded"
          data-testid={`select-user-${u.user_id}`}
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-sm font-medium">@{u.handle || 'no-handle'}</p>
          <span className="text-xs text-muted-foreground">{u.name || ''}</span>
          {u.role && <Badge variant="outline" className="text-[9px]">{u.role}</Badge>}
          {isUncertain && <Badge className="bg-amber-500/20 text-amber-400 text-[9px]">Review</Badge>}
        </div>
        <p className="text-[10px] text-muted-foreground">{u.email} | Created: {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'unknown'}</p>
        <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
          <span>{u.associated_data?.posts || 0} posts</span>
          <span>{u.associated_data?.tracks || 0} tracks</span>
          <span>{u.associated_data?.follows || 0} follows</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-1">
          {u.reasons?.map((r, i) => (
            <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400">{r}</span>
          ))}
        </div>
        {u.note && <p className="text-[10px] text-amber-400 mt-1">{u.note}</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background" data-testid="admin-cleanup-page">
      <div className="bg-card border-b border-border sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <UserMinus className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold">User Cleanup</h1>
                <p className="text-sm text-muted-foreground">Identify and remove test/demo accounts</p>
              </div>
            </div>
            <Button
              onClick={identifyTestUsers}
              disabled={loading}
              className="bg-amber-500 hover:bg-amber-500/80 text-black font-semibold"
              data-testid="scan-test-users-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Search className="w-4 h-4 mr-1" />}
              Scan for Test Users
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Cleanup Report */}
        {report && (
          <Card className="bg-green-500/5 border-green-500/20 p-6" data-testid="cleanup-report">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <h3 className="font-bold text-lg text-green-400">Cleanup Complete</h3>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {Object.entries(report).map(([k, v]) => (
                <div key={k} className="text-center">
                  <p className="text-2xl font-bold">{v}</p>
                  <p className="text-[10px] text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Scan Results */}
        {results && (
          <>
            {/* Summary */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="bg-red-500/5 border-red-500/20 p-4 text-center">
                <p className="text-3xl font-bold text-red-400">{results.test_count}</p>
                <p className="text-xs text-muted-foreground">Test Users Found</p>
              </Card>
              <Card className="bg-amber-500/5 border-amber-500/20 p-4 text-center">
                <p className="text-3xl font-bold text-amber-400">{results.uncertain_count}</p>
                <p className="text-xs text-muted-foreground">Uncertain (Review)</p>
              </Card>
              <Card className="bg-green-500/5 border-green-500/20 p-4 text-center">
                <p className="text-3xl font-bold text-green-400">{results.total_real_users}</p>
                <p className="text-xs text-muted-foreground">Real Users</p>
              </Card>
            </div>

            {/* Test Users */}
            {results.test_users?.length > 0 && (
              <Card className="bg-card border-border p-5" data-testid="test-users-list">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-sm text-red-400">Test/Demo Users ({results.test_users.length})</h3>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={results.test_users.every(u => selected.has(u.user_id))}
                      onChange={() => {
                        const allIds = results.test_users.map(u => u.user_id);
                        setSelected(prev => {
                          const next = new Set(prev);
                          const allSelected = allIds.every(id => next.has(id));
                          allIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
                          return next;
                        });
                      }}
                      className="rounded"
                    />
                    Select All
                  </label>
                </div>
                <div className="space-y-2">
                  {results.test_users.map(u => <UserRow key={u.user_id} u={u} />)}
                </div>
              </Card>
            )}

            {/* Uncertain Users */}
            {results.uncertain_users?.length > 0 && (
              <Card className="bg-card border-amber-500/20 p-5" data-testid="uncertain-users-list">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <h3 className="font-bold text-sm text-amber-400">Uncertain Users — Review Carefully ({results.uncertain_users.length})</h3>
                </div>
                <div className="space-y-2">
                  {results.uncertain_users.map(u => <UserRow key={u.user_id} u={u} isUncertain />)}
                </div>
              </Card>
            )}

            {/* No test users */}
            {results.test_count === 0 && results.uncertain_count === 0 && (
              <Card className="bg-green-500/5 border-green-500/20 p-6 text-center" data-testid="no-test-users">
                <Shield className="w-10 h-10 mx-auto mb-2 text-green-400" />
                <p className="text-lg font-bold text-green-400">Database is Clean</p>
                <p className="text-sm text-muted-foreground">No test or demo accounts found. Only {results.total_real_users} real users remain.</p>
              </Card>
            )}

            {/* Remove Button */}
            {selected.size > 0 && (
              <div className="sticky bottom-4 z-50">
                <Card className="bg-red-500/10 border-red-500/30 p-4 flex items-center justify-between">
                  <p className="text-sm">
                    <span className="font-bold text-red-400">{selected.size}</span> user(s) selected for removal
                  </p>
                  <Button
                    onClick={removeSelected}
                    disabled={removing}
                    className="bg-red-600 hover:bg-red-700 text-foreground font-semibold"
                    data-testid="remove-selected-btn"
                  >
                    {removing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                    Remove {selected.size} User(s)
                  </Button>
                </Card>
              </div>
            )}
          </>
        )}

        {/* Initial State */}
        {!results && !report && !loading && (
          <Card className="bg-card border-border p-8 text-center" data-testid="cleanup-initial">
            <Users className="w-12 h-12 mx-auto mb-3 text-muted-foreground/80" />
            <p className="text-lg font-bold mb-1">User Cleanup Tool</p>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Scan the database to identify test, demo, and seeded accounts. Review the results before confirming removal.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
